/**
 * UpdateBanner — notification when a new service worker version has been
 * downloaded and is ready to activate. Accent-coloured card with an inline
 * action button.
 *
 * Rendered as a FIXED floating card pinned to the top-centre of the viewport
 * (not an in-flow strip), so it stays visible no matter how far the user has
 * scrolled — an in-flow banner at the document top scrolls out of sight on
 * long pages, hiding the "refresh to update" CTA. Top placement keeps it
 * clear of the bottom-anchored toast stack (zIndex 2000). The anchoring and
 * the card's look both live in <TopBannerHost>, which owns the one
 * <ViewportLayer> the top banners share: the card tracks the VISIBLE top edge
 * (zoom, keyboard) and the top safe-area inset the same way the toast stack
 * tracks the bottom one, and it stacks above <InstallBanner> instead of
 * covering it.
 *
 * Mounted once at the App root (inside ToastProvider). Renders nothing until
 * useServiceWorkerUpdate flips `updateAvailable` to true. Stays visible until
 * the user taps "refresh" — there is no auto-dismiss because the alternative
 * is silently running stale code.
 */
import React, { useState } from "react";
import { TopBanner, TopBannerCard, TopBannerAction, TOP_BANNER_ORDER } from "./TopBannerHost";
import { FocusLayer } from "./ui";
import { LoadingBlank } from "./LoadingSkeleton";
import { useTranslation } from "../i18n";
import { useServiceWorkerUpdate } from "../hooks/useServiceWorkerUpdate";

/** Above the top banners (2001) and the forced-update modal (2100), below the cookie strip. */
export const UPDATING_Z = 2200;

export function UpdateBanner() {
  const { t } = useTranslation();
  const { updateAvailable, applyUpdate } = useServiceWorkerUpdate();
  const [updating, setUpdating] = useState(false);
  // The reload after the tap can wait (a worker in `activating` queues it) and
  // the old page would stay on screen with no feedback — so the first tap
  // covers the window with the branded loader at once, until the page unloads
  // (docs/spec/01c-sw-update-flow.md, "Feedback after the tap").
  const onRefresh = () => {
    if (updating) return;
    setUpdating(true);
    applyUpdate();
  };
  if (updating) {
    return (
      <FocusLayer zIndex={UPDATING_Z}>
        <LoadingBlank stage="update" />
      </FocusLayer>
    );
  }
  if (!updateAvailable) return null;
  return (
    <TopBanner order={TOP_BANNER_ORDER.update}>
      <TopBannerCard role="alert">
        <span>{t("update.available")}</span>
        <TopBannerAction onClick={onRefresh}>{t("update.refresh")}</TopBannerAction>
      </TopBannerCard>
    </TopBanner>
  );
}
